/**
 * M3X.WORKSPACE F.13 — Attention signals aggregator.
 *
 * Polls each sub-domain's `countSource()` every 60s via setInterval
 * (idle-callback wrapper is optional). Returns a Map<subDomainId,
 * count> via `.bySubDomain`, plus `.byScreen` for tab-level chips.
 *
 * M3X.WORKSPACE-CLOSURE Session 2 PR 4 — extended to expose per-
 * screen counts so ContentTabStrip can render its own chips per
 * tab. The sub-domain count remains the SUM of its screens' counts.
 */

import { useEffect, useState } from "react";

import type { AttentionCountContext } from "../lib/attentionCounts";
import {
  useLegalEntityContext,
  useLegalEntityId,
} from "../lib/legalEntity";
import { NAVIGATION_REGISTRY } from "../navigation/registry";


export type AttentionMap = Record<string, number>;


export interface AttentionSignals {
  /** Count per sub-domain id (sum of its screens' counts). */
  bySubDomain: AttentionMap;
  /** Count per screen id (raw). */
  byScreen: AttentionMap;
}


export function useAttentionSignals(
  refreshIntervalMs = 60_000,
): AttentionSignals {
  const legalEntityId = useLegalEntityId();
  const { isLoading, entitiesLoading, noEntities } = useLegalEntityContext();
  const countsReady =
    !!legalEntityId && !isLoading && !entitiesLoading && !noEntities;

  const [signals, setSignals] = useState<AttentionSignals>({
    bySubDomain: {},
    byScreen: {},
  });

  useEffect(() => {
    if (!countsReady) {
      setSignals({ bySubDomain: {}, byScreen: {} });
      return;
    }

    let cancelled = false;
    const ctx: AttentionCountContext = { legalEntityId };

    async function refresh() {
      const bySubDomain: AttentionMap = {};
      const byScreen: AttentionMap = {};
      for (const sd of NAVIGATION_REGISTRY) {
        if (sd.countSource) {
          try {
            const c = await sd.countSource(ctx);
            bySubDomain[sd.id] = c;
          } catch {
            // count source failures are non-fatal
          }
        }
        // Per-screen counts, summed into sub-domain
        for (const screen of sd.screens) {
          if (screen.countSource) {
            try {
              const c = await screen.countSource(ctx);
              byScreen[screen.id] = c;
              bySubDomain[sd.id] = (bySubDomain[sd.id] ?? 0) + c;
            } catch {
              // ignore
            }
          }
        }
      }
      if (!cancelled) setSignals({ bySubDomain, byScreen });
    }

    refresh();
    const id = setInterval(refresh, refreshIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [refreshIntervalMs, countsReady, legalEntityId]);

  return signals;
}


/** Backwards-compat: aggregator that returns just the sub-domain map. */
export function useAttentionSignalsMap(
  refreshIntervalMs = 60_000,
): AttentionMap {
  const sig = useAttentionSignals(refreshIntervalMs);
  return sig.bySubDomain;
}


/** Resolve whether a module has any sub-domain with attention. */
export function moduleHasAttention(
  moduleId: string,
  counts: AttentionMap,
): boolean {
  return NAVIGATION_REGISTRY.some(
    (sd) => sd.module === moduleId && (counts[sd.id] ?? 0) > 0,
  );
}
